import { action, observable, runInAction, makeAutoObservable } from 'mobx';
import jwtDecode from 'jwt-decode';
import { redirectToLogin } from './authentication';
import { DisplayedOktaScopes, OktaScopes, OktaScopesConfig } from './okta-auth-api.consts';
import { SessionStorageKeys } from './session-storage';

export interface UserData {
  id?: string;
  $first_name?: string;
  $last_name?: string;
  $email?: string;
}

export interface UserApiTokens {
  accessToken: string;
  refreshToken: string;
}

export class UserStore {
  areSettingsLoaded: boolean = false;
  userData: UserData = undefined;
  userOktaGroupsMap: { [group: string]: boolean };
  requestedOktaScope: OktaScopes = sessionStorage.getItem(SessionStorageKeys.REQUESTED_OKTA_SCOPE) as OktaScopes;
  userApiTokens: { [oktaScope: string]: UserApiTokens } = {};

  constructor() {
    makeAutoObservable(this);
  }

  updateUserSettings = async (accessToken?: string) => {
    if (!accessToken) {
      runInAction(() => {
        this.areSettingsLoaded = true;
      });
      return;
    }

    let oktaTokenPayload;

    try {
      oktaTokenPayload = jwtDecode(accessToken);
    } catch (err) {
      redirectToLogin(true);
      return;
    }

    const {
      uid,
      sub: $email,
      user_first_name: $first_name,
      user_last_name: $last_name,
      groups,
      permitted_areas,
      permitted_road_types,
    } = oktaTokenPayload;
    const userData = { id: uid, $first_name, $last_name, $email };

    runInAction(() => {
      this.userData = userData;
      this.userOktaGroupsMap = (groups || []).reduce((acc, group) => {
        if (!group) {
          return acc;
        }
        acc[group] = true;
        return acc;
      }, {});
      if (!this.requestedOktaScope) {
        this.setRequestedOktaScope(this.displayedOktaScopes[0]);
      }
      this.areSettingsLoaded = true;
    });
  };

  setRequestedOktaScope = (oktaScope: OktaScopes) => {
    this.requestedOktaScope = oktaScope;
    sessionStorage.setItem(SessionStorageKeys.REQUESTED_OKTA_SCOPE, oktaScope);
  };

  setUserApiTokens = (val: UserApiTokens, oktaScope: OktaScopes) => {
    this.userApiTokens[oktaScope] = val && val.accessToken && val.refreshToken ? val : undefined;
  };

  /* Selectors */

  get displayedOktaScopes(): OktaScopes[] {
    if (!this.userOktaGroupsMap || !Object.keys(this.userOktaGroupsMap).length) {
      return [];
    }
    return DisplayedOktaScopes.filter((oktaScope) => {
      return this.userOktaGroupsMap[OktaScopesConfig[oktaScope].group];
    });
  }
}
